import React, { useEffect, useState } from "react";
import {
  Grid,
  Box,
  Typography,
  Button,
  TextField,
  Divider,
} from "@mui/material";
import * as pdfjsLib from "pdfjs-dist";
import qrImg from "../../images/qr.jpeg";
import MultipleStopIcon from "@mui/icons-material/MultipleStop";
import { BASE_URL } from "../../constants";

// Serve the worker from this app so PDF parsing does not depend on a runtime CDN.
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.mjs`;

const sectionStyle = {
  background: "#fff",
  padding: 12,
  borderRadius: 8,
  boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
  marginBottom: 16,
};

const grid2 = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 10,
};

const inputStyle = {
  width: "100%",
  height: 34,
  padding: "4px 6px",
  borderRadius: 6,
  border: "1px solid #ccc",
  fontSize: 13,
};

const labelStyle = {
  fontSize: 11,
  color: "#555",
  marginBottom: 2,
};

export default function MicrobiologyReportLayoutGuide() {
  /* ================= PDF ================= */
  const [viewport, setViewport] = useState(null);
  const [zoom, setZoom] = useState(1);

  /* ================= HEADER ================= */
  const [headerX, setHeaderX] = useState(33);
  const [headerY, setHeaderY] = useState(140);
  const [headerWidth, setHeaderWidth] = useState(555);
  const [headerHeight, setHeaderHeight] = useState(96);
  const [colWidths, setColWidths] = useState([247, 200, 108]);

  /* ================= CONTENT ================= */
  const [contentX, setContentX] = useState(30);
  const [contentY, setContentY] = useState(247);
  const [contentWidth, setContentWidth] = useState(556);
  const [contentHeight, setContentHeight] = useState(484);

  /* ================= FOOTER SIGNATURES ================= */
  const SIGN_W = 94;
  const SIGN_H = 60;

  const [techSignX, setTechSignX] = useState(383);
  const [techSignY, setTechSignY] = useState(744); // set after pdf load

  const [docSignX, setDocSignX] = useState(492);
  const [docSignY, setDocSignY] = useState(744); // set after pdf load

  /* ================= DRAG ================= */
  const [dragging, setDragging] = useState(null);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const loadPdfFromUrl = async (pdfUrl) => {
    try {
      const loadingTask = pdfjsLib.getDocument(pdfUrl);
      const pdf = await loadingTask.promise;
      const page = await pdf.getPage(1);

      const vp = page.getViewport({ scale: 1 });
      setViewport(vp);

      const canvas = document.getElementById("pdf-canvas");
      const ctx = canvas.getContext("2d");

      canvas.width = vp.width;
      canvas.height = vp.height;

      await page.render({
        canvasContext: ctx,
        viewport: vp,
      }).promise;
    } catch (err) {
      console.error("❌ Failed to load PDF from URL:", err);
    }
  };

  const applyLayoutToState = (layout) => {
    if (!layout) return;

    /* ================= HEADER ================= */
    if (layout.header) {
      setHeaderX(layout.header.position?.x ?? headerX);
      setHeaderY(layout.header.position?.y ?? headerY);
      setHeaderWidth(layout.header.size?.width ?? headerWidth);

      const cols = layout.header.columns;
      if (cols) {
        setColWidths([
          cols.patient?.width ?? colWidths[0],
          cols.uhid?.width ?? colWidths[1],
          cols.qr?.width ?? colWidths[2],
        ]);
      }
    }

    /* ================= CONTENT ================= */
    if (layout.content) {
      setContentX(layout.content.position?.x ?? contentX);
      setContentY(layout.content.position?.y ?? contentY);
      setContentWidth(layout.content.size?.width ?? contentWidth);
      setContentHeight(layout.content.size?.height ?? contentHeight);
    }

    /* ================= FOOTER ================= */
    if (layout.footer) {
      const tech = layout.footer.technicianSignature;
      const doc = layout.footer.doctorSignature;

      if (tech) {
        setTechSignX(tech.position?.x ?? techSignX);
        setTechSignY(tech.position?.y ?? techSignY);
      }

      if (doc) {
        setDocSignX(doc.position?.x ?? docSignX);
        setDocSignY(doc.position?.y ?? docSignY);
      }
    }
  };

  const fetchTemplatePdf = async () => {
    try {
      const res = await fetch(`${BASE_URL}api/org/report-template`, {
        headers: {
        },
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.message || "Failed to load template PDF");
      }
      console.log(data);
      const pdfUrl = data.reportTemplate;

      if (pdfUrl) {
        await loadPdfFromUrl(pdfUrl);
      }
    } catch (err) {
      console.error("❌ Error loading template PDF:", err);
    }
  };

  const fetchTemplatePdfLayout = async () => {
    try {
      const res = await fetch(`${BASE_URL}api/org/micro-layout`, {
        headers: {
        },
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.message || "Failed to load layout");
      }

      if (data.layout) {
        applyLayoutToState(data.layout);
        console.log("✅ Layout applied from backend");
      }
    } catch (err) {
      console.error("❌ Error loading layout:", err);
    }
  };

  useEffect(() => {
    fetchTemplatePdf();
    fetchTemplatePdfLayout();
  }, []);

  /* ================= LOAD PDF ================= */
  const loadPdf = async (file) => {
    if (!file) return;
    const buffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: buffer }).promise;
    const page = await pdf.getPage(1);
    const vp = page.getViewport({ scale: 1 });
    setViewport(vp);

    const canvas = document.getElementById("pdf-canvas");
    const ctx = canvas.getContext("2d");
    canvas.width = vp.width;
    canvas.height = vp.height;

    await page.render({ canvasContext: ctx, viewport: vp }).promise;
    canvas.style.width = `${vp.width * zoom}px`;
    canvas.style.height = `${vp.height * zoom}px`;
  };

  /* ================= START DRAG ================= */
  const startDrag = (e, type, x, y) => {
    const rect = document
      .getElementById("canvasWrapper")
      .getBoundingClientRect();

    setOffset({
      x: e.clientX - rect.left - x * zoom,
      y: e.clientY - rect.top - y * zoom,
    });
    setDragging(type);
    e.stopPropagation();
  };

  /* ================= MOUSE MOVE ================= */
  const handleMouseMove = (e) => {
    if (!dragging || !viewport) return;

    const rect = document
      .getElementById("canvasWrapper")
      .getBoundingClientRect();

    const mouseX = (e.clientX - rect.left) / zoom;
    const mouseY = (e.clientY - rect.top) / zoom;

    /* HEADER MOVE */
    if (dragging === "header") {
      setHeaderX(
        Math.max(
          0,
          Math.min(mouseX - offset.x / zoom, viewport.width - headerWidth),
        ),
      );
      setHeaderY(
        Math.max(
          0,
          Math.min(mouseY - offset.y / zoom, viewport.height - headerHeight),
        ),
      );
    }

    /* HEADER RESIZE (WIDTH + HEIGHT) */
    /* HEADER RESIZE (WIDTH ONLY) */
    if (dragging === "header-resize") {
      const newW = Math.max(300, mouseX - headerX);

      const scale = newW / headerWidth;
      setHeaderWidth(Math.round(newW));
      setColWidths(colWidths.map((w) => Math.round(w * scale)));
    }

    /* CONTENT MOVE */
    if (dragging === "content") {
      setContentX(
        Math.max(
          0,
          Math.min(mouseX - offset.x / zoom, viewport.width - contentWidth),
        ),
      );
      setContentY(
        Math.max(
          0,
          Math.min(mouseY - offset.y / zoom, viewport.height - contentHeight),
        ),
      );
    }

    /* CONTENT RESIZE */
    if (dragging === "content-resize") {
      setContentWidth(Math.max(200, mouseX - contentX));
      setContentHeight(Math.max(150, mouseY - contentY));
    }

    /* COLUMN RESIZE */
    if (dragging?.startsWith("col-")) {
      const i = Number(dragging.split("-")[1]);
      const leftEdge =
        headerX + colWidths.slice(0, i).reduce((a, b) => a + b, 0);

      const newW = mouseX - leftEdge;
      const min = 80;

      if (newW > min && colWidths[i + 1] > min) {
        const diff = newW - colWidths[i];
        const cols = [...colWidths];
        cols[i] += diff;
        cols[i + 1] -= diff;
        setColWidths(cols.map((v) => Math.round(v)));
      }
    }
    /* QR COLUMN RESIZE (LAST COLUMN) */
    if (dragging === "col-qr") {
      const min = 80;

      // Left edge of QR column
      const qrLeft = headerX + colWidths[0] + colWidths[1];

      const newQrWidth = mouseX - qrLeft;

      if (newQrWidth > min) {
        const newCols = [...colWidths];
        newCols[2] = Math.round(newQrWidth);

        setColWidths(newCols);

        // Header width = sum of all columns
        setHeaderWidth(newCols.reduce((a, b) => a + b, 0));
      }
    }

    /* TECH SIGN MOVE */
    if (dragging === "tech-sign") {
      setTechSignX(
        Math.max(
          0,
          Math.min(mouseX - offset.x / zoom, viewport.width - SIGN_W),
        ),
      );
      setTechSignY(
        Math.max(
          0,
          Math.min(mouseY - offset.y / zoom, viewport.height - SIGN_H),
        ),
      );
    }

    /* DOCTOR SIGN MOVE */
    if (dragging === "doc-sign") {
      setDocSignX(
        Math.max(
          0,
          Math.min(mouseX - offset.x / zoom, viewport.width - SIGN_W),
        ),
      );
      setDocSignY(
        Math.max(
          0,
          Math.min(mouseY - offset.y / zoom, viewport.height - SIGN_H),
        ),
      );
    }
  };

  const stopDrag = () => setDragging(null);

  const getHeaderColumns = () => {
    return colWidths.map((w, index) => ({
      index,
      width: w,
      percent: ((w / headerWidth) * 100).toFixed(2),
    }));
  };

  /* ================= SAVE ================= */
  const saveLayout = async () => {
    try {
      const layoutConfig = {
        header: {
          position: { x: headerX, y: headerY },
          size: { width: headerWidth, height: headerHeight },
          columns: {
            patient: { width: colWidths[0] },
            uhid: { width: colWidths[1] },
            qr: { width: colWidths[2] },
          },
        },
        content: {
          position: { x: contentX, y: contentY },
          size: { width: contentWidth, height: contentHeight },
        },
        footer: {
          technicianSignature: {
            position: { x: techSignX, y: techSignY },
            size: { width: SIGN_W, height: SIGN_H },
          },
          doctorSignature: {
            position: { x: docSignX, y: docSignY },
            size: { width: SIGN_W, height: SIGN_H },
          },
        },
      };

      const res = await fetch(`${BASE_URL}api/org/micro-layout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(layoutConfig),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.message || "Failed to save layout");
      }

      alert("Microbiology layout saved successfully!");
    } catch (error) {
      alert("Failed to save layout");
    }
  };

  /* ================= RENDER ================= */
  return (
    <Grid container sx={{ height: "100vh" }}>
      <Grid item xs={9} sx={{ p: 2, borderRight: "1px solid #ccc" }}>
        {/* <Box sx={{ display: "flex", gap: 2 }}>
          <Button variant="contained" component="label">
            Choose PDF
            <input
              hidden
              type="file"
              accept="application/pdf"
              onChange={async (e) => {
                const file = e.target.files[0];
                const validation = await validateFile(file, FILE_RULES.pdf);
                if (!validation.valid) {
                  alert(validation.error);
                  e.target.value = "";
                  return;
                }
                loadPdf(file);
              }}
            />
          </Button>
        </Box> */}

        <div
          id="canvasWrapper"
          onMouseMove={handleMouseMove}
          onMouseUp={stopDrag}
          onMouseLeave={stopDrag}
          style={{
            position: "relative",
            width: viewport ? viewport.width * zoom : "auto",
            height: viewport ? viewport.height * zoom : "auto",
            margin: "20px auto",
            background: viewport ? "#fff" : "#eee",
            boxShadow: "0px 0px 12px 1px black",
          }}
        >
          <canvas id="pdf-canvas" />

          {/* ================= HEADER ================= */}
          {viewport && (
            <div
              onMouseDown={(e) => startDrag(e, "header", headerX, headerY)}
              style={{
                position: "absolute",
                left: headerX * zoom,
                top: headerY * zoom,
                width: headerWidth * zoom,
                height: headerHeight * zoom,
                display: "flex",
                background: "rgba(0,120,255,0.12)",
                border: "2px dashed #0078ff",
                cursor: "move",
              }}
            >
              {colWidths.map((w, i) => (
                <div
                  key={i}
                  style={{
                    width: w * zoom,
                    // padding: "3px 1px 3px 20px",
                    background:
                      i === 0
                        ? "rgba(255,255,0,0.35)"
                        : i === 1
                          ? "rgb(255 90 0 / 35%)"
                          : "rgba(0,200,0,0.35)",
                    position: "relative",
                  }}
                >
                  {i === 0 && (
                    <div style={{ fontSize: 10 * zoom, lineHeight: 1.4 }}>
                      <b style={{ fontSize: 12 * zoom }}>PATIENT NAME</b>
                      <div>Sex / Age : Female / 37Y 0M 16D</div>
                      <div>Mobile : 9999999999</div>
                      <div>Ref. By : Dr. Doctor Name</div>
                      <div>Center : 0-0-0</div>
                      <div>Lab ID : 00.00</div>
                    </div>
                  )}

                  {i === 1 && (
                    <>
                      <ResizeHandleIcon side="left" />
                      <ResizeHandleIcon side="right" />

                      <div
                        style={{
                          fontSize: "8.2px",
                          padding: "0px 0px 0px 20px",
                          lineHeight: "15.5px",
                        }}
                      >
                        <div>
                          <span>UHID</span>
                          <span
                            style={{
                              marginLeft: "57px",
                              fontWeight: "600",
                              fontSize: "10px",
                            }}
                          >
                            : OP01264
                          </span>
                        </div>

                        <div>
                          <span>Case ID</span>
                          <span
                            style={{
                              marginLeft: "46px",
                              fontWeight: "600",
                              fontSize: "8px",
                            }}
                          >
                            : CI01266
                          </span>
                        </div>

                        <div>
                          <span>Sample Collected</span>
                          <span style={{ marginLeft: "9px" }}>
                            : 16-Jan-26 04:00 PM
                          </span>
                        </div>

                        <div>
                          <span>Report Generated</span>
                          <span style={{ marginLeft: "6px" }}>
                            : 16-Jan-26 06:58 PM
                          </span>
                        </div>

                        <div
                          style={{
                            margin: "3px 0px 0px 2px",
                            height: "18px",
                            width: "164px",
                            background:
                              "repeating-linear-gradient(90deg,#000 0,#000 2px,#fff 2px,#fff 4px)",
                          }}
                        />
                      </div>
                    </>
                  )}

                  {/* {i === 2 && (
                    <img
                      src={qrImg}
                      style={{
                        margin: "15px 0px 0px 21px",
                        width: "72px",
                        height: "66px",
                      }}
                    />
                  )} */}
                  {i === 2 && (
                    <>
                      <img
                        src={qrImg}
                        style={{
                          margin: "15px 0px 0px 21px",
                          width: "72px",
                          height: "66px",
                        }}
                      />

                      {/* QR RIGHT RESIZE HANDLE */}
                      <div
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          setDragging("col-qr"); // 🔑 special case
                        }}
                        style={{
                          position: "absolute",
                          right: -6,
                          top: 0,
                          width: 10,
                          height: "100%",
                          cursor: "ew-resize",
                          color: "#000",
                        }}
                      />

                      {/* Visual icon */}
                      <MultipleStopIcon
                        sx={{
                          position: "absolute",
                          top: "50%",
                          right: -9,
                          fontSize: 18,
                          color: "#000",
                          //   opacity: 0.7,
                          pointerEvents: "none",
                        }}
                      />
                    </>
                  )}

                  {i < colWidths.length - 1 && (
                    <div
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        setDragging(`col-${i}`);
                      }}
                      style={{
                        position: "absolute",
                        right: -4,
                        top: 0,
                        width: 8,
                        height: "100%",
                        cursor: "ew-resize",
                      }}
                    />
                  )}
                </div>
              ))}

              {/* HEADER RESIZE HANDLE */}
              <div
                onMouseDown={(e) => {
                  e.stopPropagation();
                  setDragging("header-resize");
                }}
                style={{
                  position: "absolute",
                  right: -8,
                  bottom: -8,
                  width: 16,
                  height: 16,
                  background: "#0078ff",
                  cursor: "nwse-resize",
                }}
              />
            </div>
          )}

          {/* ================= CONTENT ================= */}
          {viewport && (
            <div
              onMouseDown={(e) => startDrag(e, "content", contentX, contentY)}
              style={{
                position: "absolute",
                left: contentX * zoom,
                top: contentY * zoom,
                width: contentWidth * zoom,
                height: contentHeight * zoom,
                background: "rgb(233 0 255 / 18%)",
                border: "2px dashed #db00ff",
                cursor: "move",
              }}
            >
              <div
                style={{
                  padding: 8 * zoom,
                  fontSize: 10 * zoom,
                  lineHeight: 1.5,
                }}
              >
                {/* TITLE */}
                <div
                  style={{
                    textAlign: "center",
                    fontWeight: "bold",
                    fontSize: 12 * zoom,
                    marginBottom: 8 * zoom,
                  }}
                >
                  SPUTUM FOR AFB
                </div>

                {/* SPECIMEN */}
                <div style={{ marginBottom: 6 * zoom }}>
                  <b>SPECIMEN :</b> URINE
                </div>

                {/* ORGANISM */}
                <div style={{ marginBottom: 6 * zoom }}>
                  <b>ORGANISM :</b> qwe
                </div>

                {/* COLONY COUNT */}
                <div style={{ marginBottom: 10 * zoom }}>
                  <b>COLONY COUNT :</b> 1234
                </div>

                {/* ANTIBIOTIC SENSITIVITY TABLE */}
                <div style={{ marginTop: 10 * zoom }}>
                  <b>ANTIBIOTIC SENSITIVITY :</b>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "2fr 1fr 1fr 1fr",
                      marginTop: 6,
                      borderBottom: "1px solid #000",
                      fontWeight: "bold",
                    }}
                  >
                    <div>ANTIBIOTIC</div>
                    <div>S</div>
                    <div>I</div>
                    <div>R</div>
                  </div>

                  {[
                    "Amikacin",
                    "Ciprofloxacin",
                    "Gentamicin",
                    "Levofloxacin",
                    "Tetracycline",
                  ].map((drug, i) => (
                    <div
                      key={i}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "2fr 1fr 1fr 1fr",
                        borderBottom: "1px dashed #ccc",
                        padding: "2px 0",
                      }}
                    >
                      <div>{drug}</div>
                      <div></div>
                      <div></div>
                      <div></div>
                    </div>
                  ))}
                </div>

                {/* INTERPRETATION */}
                <div style={{ marginTop: 10 * zoom }}>
                  <b>INTERPRETATION :</b>
                  <div style={{ paddingLeft: 10 }}>
                    Growth detected. Clinical correlation advised.
                  </div>
                </div>

                {/* ADDITIONAL NOTES */}
                <div style={{ marginTop: 10 * zoom }}>
                  <b>NOTES :</b>
                  <div style={{ paddingLeft: 10 }}>
                    Sample processed under sterile conditions.
                  </div>
                </div>

                {/* END */}
                <div
                  style={{
                    marginTop: 8 * zoom,
                    textAlign: "center",
                    fontStyle: "italic",
                    color: "#666",
                  }}
                >
                  *** End of Report ***
                </div>

                {/* DISCLAIMER */}
                <div style={{ fontSize: 8, marginTop: 10, color: "#555" }}>
                  This report is not meant for medico-legal purposes. The test
                  is performed with analytical procedures. Result need to be
                  correlated with clinical findings for final diagnosis. Reports
                  related only to the sample submitted.
                </div>
              </div>

              {/* CONTENT RESIZE HANDLE */}
              <div
                onMouseDown={(e) => {
                  e.stopPropagation();
                  setDragging("content-resize");
                }}
                style={{
                  position: "absolute",
                  right: -8,
                  bottom: -8,
                  width: 16,
                  height: 16,
                  background: "rgb(219, 0, 255)",
                  cursor: "nwse-resize",
                }}
              />
            </div>
          )}

          {/* ================= TECHNICIAN SIGNATURE ================= */}
          {viewport && (
            <div
              onMouseDown={(e) =>
                startDrag(e, "tech-sign", techSignX, techSignY)
              }
              style={{
                position: "absolute",
                left: techSignX,
                top: techSignY,
                width: SIGN_W,
                height: SIGN_H,
                background: "rgba(0,0,0,0.08)",
                border: "2px dashed #555",
                cursor: "move",
                display: "flex",
                alignItems: "end",
                fontSize: "8px",
                fontWeight: 600,
              }}
            >
              <div>
                {/* <div
                  style={{ height: 22 * zoom, borderBottom: "1px solid #000" }}
                /> */}
                <div>lab tech</div>
                <div>line2</div>
                <div>line3</div>
                <div>line4</div>
              </div>
            </div>
          )}

          {/* ================= DOCTOR SIGNATURE ================= */}
          {viewport && (
            <div
              onMouseDown={(e) => startDrag(e, "doc-sign", docSignX, docSignY)}
              style={{
                position: "absolute",
                left: docSignX,
                top: docSignY,
                width: SIGN_W,
                height: SIGN_H,
                background: "rgba(0,0,0,0.08)",
                border: "2px dashed #555",
                cursor: "move",
                display: "flex",
                alignItems: "end",
                fontSize: "7px",
                fontWeight: 600,
              }}
            >
              <div>
                {/* <div
                  style={{ height: 22 * zoom, borderBottom: "1px solid #000" }}
                /> */}
                {/* <div>Consultant Pathologist</div> */}
                <div>doctor</div>
                <div>line2</div>
                <div>line3</div>
                <div>line4</div>
              </div>
            </div>
          )}
        </div>
      </Grid>

      {/* RIGHT PANEL */}
      {viewport && (
        <Grid
          item
          xs={3}
          sx={{ p: 2, backgroundColor: "#f7f9fc", overflowY: "auto" }}
        >
          <Typography variant="h6" gutterBottom>
            Layout Settings
          </Typography>

          {/* ================= HEADER ================= */}
          <Typography variant="subtitle2" gutterBottom>
            Header Box
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="Header X"
                size="small"
                fullWidth
                type="number"
                value={headerX}
                sx={{ backgroundColor: "rgb(0 120 255 / 25%)" }}
                onChange={(e) => setHeaderX(+e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Header Y"
                size="small"
                fullWidth
                type="number"
                value={headerY}
                sx={{ backgroundColor: "rgb(0 120 255 / 25%)" }}
                onChange={(e) => setHeaderY(+e.target.value)}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="Header Width"
                size="small"
                fullWidth
                type="number"
                value={headerWidth}
                sx={{ backgroundColor: "rgb(0 120 255 / 25%)" }}
                onChange={(e) => {
                  const newW = +e.target.value;
                  const scale = newW / headerWidth;
                  setHeaderWidth(newW);
                  setColWidths(colWidths.map((w) => Math.round(w * scale)));
                }}
              />
            </Grid>
            {/* <Grid item xs={6}>
            <TextField
              label="Header Height"
              size="small"
              fullWidth
              type="number"
              value={headerHeight}
              disabled
            />
          </Grid> */}
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* ================= HEADER COLUMNS ================= */}
          <Typography variant="subtitle2" gutterBottom>
            Header Columns
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="Patient Width"
                size="small"
                fullWidth
                type="number"
                value={colWidths[0]}
                sx={{ backgroundColor: "rgba(255,255,0,0.35)" }}
                onChange={(e) => {
                  const cols = [...colWidths];
                  cols[0] = +e.target.value;
                  setColWidths(cols);
                  setHeaderWidth(cols.reduce((a, b) => a + b, 0));
                }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="UHID Width"
                size="small"
                fullWidth
                type="number"
                value={colWidths[1]}
                sx={{ backgroundColor: "rgb(255 90 0 / 35%)" }}
                onChange={(e) => {
                  const cols = [...colWidths];
                  cols[1] = +e.target.value;
                  setColWidths(cols);
                  setHeaderWidth(cols.reduce((a, b) => a + b, 0));
                }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="QR Width"
                size="small"
                fullWidth
                type="number"
                value={colWidths[2]}
                sx={{ backgroundColor: "rgba(0,200,0,0.35)" }}
                onChange={(e) => {
                  const cols = [...colWidths];
                  cols[2] = +e.target.value;
                  setColWidths(cols);
                  setHeaderWidth(cols.reduce((a, b) => a + b, 0));
                }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* ================= CONTENT ================= */}
          <Typography variant="subtitle2" gutterBottom>
            Content Box
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="Content X"
                size="small"
                fullWidth
                type="number"
                value={contentX}
                sx={{ backgroundColor: "rgb(233 0 255 / 18%)" }}
                onChange={(e) => setContentX(+e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Content Y"
                size="small"
                fullWidth
                type="number"
                value={contentY}
                sx={{ backgroundColor: "rgb(233 0 255 / 18%)" }}
                onChange={(e) => setContentY(+e.target.value)}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                label="Content Width"
                size="small"
                fullWidth
                type="number"
                value={contentWidth}
                sx={{ backgroundColor: "rgb(233 0 255 / 18%)" }}
                onChange={(e) => setContentWidth(+e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Content Height"
                size="small"
                fullWidth
                type="number"
                value={contentHeight}
                sx={{ backgroundColor: "rgb(233 0 255 / 18%)" }}
                onChange={(e) => setContentHeight(+e.target.value)}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* ================= FOOTER ================= */}
          <Typography variant="subtitle2" gutterBottom>
            Footer Signatures
          </Typography>

          <Typography variant="caption">Technician</Typography>
          <Grid container spacing={2} mb={1}>
            <Grid item xs={6}>
              <TextField
                label="X"
                size="small"
                fullWidth
                type="number"
                value={techSignX}
                sx={{ backgroundColor: "#8080802e" }}
                onChange={(e) => setTechSignX(+e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Y"
                size="small"
                fullWidth
                type="number"
                value={techSignY}
                sx={{ backgroundColor: "#8080802e" }}
                onChange={(e) => setTechSignY(+e.target.value)}
              />
            </Grid>
          </Grid>

          <Typography variant="caption">Doctor</Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="X"
                size="small"
                fullWidth
                type="number"
                value={docSignX}
                sx={{ backgroundColor: "#8080802e" }}
                onChange={(e) => setDocSignX(+e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Y"
                size="small"
                fullWidth
                type="number"
                value={docSignY}
                sx={{ backgroundColor: "#8080802e" }}
                onChange={(e) => setDocSignY(+e.target.value)}
              />
            </Grid>
          </Grid>

          <Button
            variant="contained"
            fullWidth
            sx={{ mt: 2 }}
            onClick={saveLayout}
          >
            Save Layout
          </Button>
        </Grid>
      )}
    </Grid>
  );
}

const ResizeHandleIcon = ({ side }) => (
  <MultipleStopIcon
    sx={{
      position: "absolute",
      top: "50%",
      [side]: -9,
      //   transform: "translateY(-50%) rotate(90deg)", // 🔑 vertical grip
      fontSize: 18,
      color: "#000",
      cursor: "ew-resize",
      //   opacity: 0.7,
      pointerEvents: "none", // visual only
      userSelect: "none",
    }}
  />
);
